<template>
  <div>
    <Navbar />

    <section
      class="relative w-full py-14 md:py-20 bg-[#f7fbfb] dark:bg-slate-900 overflow-hidden"
      :dir="dir"
    >
      <div class="terms-card">
        <p class="terms-intro">
          {{ t("privacy_intro_1") }}
          <span class="terms-brand">{{ t("privacy_brand") }}</span>
          {{ t("privacy_intro_2") }}
        </p>

        <div class="terms-sections">
          <article class="terms-section">
            <h3 class="terms-title">{{ t("privacy_s1_title") }}</h3>
            <ul class="terms-list">
              <li>
                <strong>{{ t("privacy_s1_item1_label") }}</strong>
                {{ t("privacy_s1_item1_text") }}
              </li>
            </ul>
          </article>

          <article class="terms-section">
            <h3 class="terms-title">{{ t("privacy_s2_title") }}</h3>
            <ul class="terms-list">
              <li>{{ t("privacy_s2_item1") }}</li>
              <!-- <li>{{ t("privacy_s2_item2") }}</li> -->
              <!-- <li>{{ t("privacy_s2_item3") }}</li> -->
              <li>{{ t("privacy_s2_item4") }}</li>
            </ul>
          </article>

          <article class="terms-section">
            <h3 class="terms-title">{{ t("privacy_s3_title") }}</h3>
            <ul class="terms-list">
              <li>{{ t("privacy_s3_item1") }}</li>
              <li>{{ t("privacy_s3_item2") }}</li>
            </ul>
          </article>

          <article class="terms-section">
            <h3 class="terms-title">{{ t("privacy_s4_title") }}</h3>
            <p class="terms-text">{{ t("privacy_s4_text") }}</p>
          </article>

          <article class="terms-section">
            <h3 class="terms-title">{{ t("privacy_s5_title") }}</h3>
            <p class="terms-text">{{ t("privacy_s5_text") }}</p>
          </article>

          <article class="terms-section">
            <h3 class="terms-title">{{ t("privacy_s6_title") }}</h3>
            <ul class="terms-list">
              <li>{{ t("privacy_s6_item1") }}</li>
            </ul>
          </article>

          <article class="terms-section">
            <h3 class="terms-title">{{ t("privacy_s7_title") }}</h3>
            <p class="terms-text">{{ t("privacy_s7_text") }}</p>
          </article>

          <article class="terms-section">
            <h3 class="terms-title">{{ t("privacy_s8_title") }}</h3>
            <p class="terms-text">{{ t("privacy_s8_text") }}</p>
          </article>

          <article class="terms-section">
            <h3 class="terms-title">{{ t("privacy_s9_title") }}</h3>
            <p class="terms-text terms-contact">{{ t("privacy_s9_text") }}</p>

            <ul class="terms-contact-list">
              <li>
                <span class="icon">📧</span>
                <strong>{{ t("privacy_email_label") }}</strong>
                info@smrt-consulting.net
              </li>
              <li>
                <span class="icon">📞</span>
                <strong>{{ t("privacy_phone_label") }}</strong>
                0509212155
              </li>
            </ul>

            <p class="terms-updated">
              {{ t("privacy_updated_label") }}: <span>{{ t("privacy_updated_date") }}</span>
            </p>
          </article>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { messages } from "@/i18n";
import Navbar from "@/components/Navbar.vue";
import Footer from "@/components/Footer.vue";

const lang = ref(
  (
    document.documentElement.lang ||
    localStorage.getItem("app_lang") ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en")
    ? "en"
    : "ar"
);

const dir = computed(() => (lang.value === "ar" ? "rtl" : "ltr"));

function t(key) {
  return (messages[lang.value] && messages[lang.value][key]) || key;
}

let langObserver = null;

onMounted(() => {
  // ✅ sync lang with <html lang="">
  langObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === "attributes" && m.attributeName === "lang") {
        const htmlLang = document.documentElement.lang || "ar";
        lang.value = htmlLang.toLowerCase().startsWith("en") ? "en" : "ar";
      }
    }
  });
  langObserver.observe(document.documentElement, { attributes: true });
});

onBeforeUnmount(() => {
  if (langObserver) langObserver.disconnect();
});
</script>

<style scoped>
.terms-card {
  background: #fff;
  border-radius: 18px;
  padding: 34px 28px;
  margin: 18px auto 28px;
  max-width: 980px;
  text-align: right;
}

[dir="ltr"] .terms-card {
  text-align: left;
}

.terms-intro {
  font-size: 14px;
  line-height: 2.1;
  color: #374151;
  margin: 0 0 18px;
}

.terms-brand {
  font-weight: 700;
}

.terms-sections {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.terms-title {
  font-size: 15px;
  font-weight: 800;
  color: #111827;
  margin: 0 0 8px;
}

.terms-text {
  font-size: 14px;
  line-height: 2.1;
  color: #374151;
  margin: 0;
}

.terms-list,
.terms-contact-list {
  margin: 0;
  padding: 0 18px 0 0;
  font-size: 14px;
  line-height: 2.1;
  color: #374151;
}

[dir="ltr"] .terms-list,
[dir="ltr"] .terms-contact-list {
  padding: 0 0 0 18px;
}

.terms-list li,
.terms-contact-list li {
  margin: 4px 0;
}

.terms-contact {
  margin-bottom: 6px;
}

.terms-contact-list .icon {
  display: inline-block;
  width: 24px;
}

.terms-updated {
  margin-top: 12px;
  font-size: 13px;
  color: #6b7280;
}

@media (max-width: 768px) {
  .terms-card {
    padding: 22px 16px;
    border-radius: 14px;
  }

  .terms-title {
    font-size: 14px;
  }

  .terms-intro,
  .terms-text,
  .terms-list,
  .terms-contact-list {
    font-size: 13px;
    line-height: 2;
  }
}
</style>